import {AxiosInstance, AxiosResponse} from "axios";
import {
    MSSanteAttachment,
    MSSanteSource,
    SecureMessagingDocumentSource,
    SecureMessagingRecipient,
    SecureMessagingRecipientSection,
    SurveySecureMessagingPrefill
} from "@/mssante/interfaces/SecureMessaging";

export interface SecureMessagingSearchParams {
    search?: string;
    first_name?: string;
    last_name?: string;
}

export interface SendSecureMessageParams {
    to: string;
    cc?: string[];
    subject: string;
    body: string;
    attachments?: MSSanteAttachment[];
    /** The patient the message is about, to show it in their follow-up. */
    consumerKey?: string;
    /** What the message was written from, to link it to a program. */
    source?: MSSanteSource;
}

export interface SendPatientSecureMessageParams {
    subject: string;
    body: string;
    attachments?: MSSanteAttachment[];
    consumerKey?: string;
    source?: MSSanteSource;
}

/**
 * Append the history context of a message: the patient and what it was written from.
 * Older HDS servers ignore these fields.
 */
function appendHistoryContext(formData: FormData, params: { consumerKey?: string, source?: MSSanteSource }): void {
    if (params.consumerKey) {
        formData.append("consumer_key", params.consumerKey);
    }

    if (params.source) {
        formData.append("sourceable_type", params.source.type);
        formData.append("sourceable_id", String(params.source.id));
    }
}

/**
 * The secure messaging (MSSanté) resource.
 *
 * @param {Object} apiClient - The injected api client, hosting the directory.
 * @param {Object} hdsClient - The injected hds client, sending the emails.
 * @returns {Object} The public object.
 */
export default function (apiClient: AxiosInstance, hdsClient: AxiosInstance) {
    return {
        /**
         * Search health professionals with a MSSanté mailbox.
         *
         * @param {SecureMessagingSearchParams} params - The name filters; `search` matches
         *     first and last names regardless of word order.
         *
         * @return {Promise<SecureMessagingRecipient[]>}
         */
        async search(params: SecureMessagingSearchParams): Promise<SecureMessagingRecipient[]> {
            return apiClient.get("/secure-messaging/search", {params})
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Get the consumer favorite recipients (the prescriptors of their
         * sales), grouped by speciality.
         *
         * @param {string} consumerKey - The consumer key.
         *
         * @return {Promise<SecureMessagingRecipientSection[]>}
         */
        async favoriteRecipients(consumerKey: string): Promise<SecureMessagingRecipientSection[]> {
            return apiClient.get(`/secure-messaging/consumers/${consumerKey}/recipients`)
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Render the messages to propose after a completed survey, in proposal
         * order. Falls back to a single generic message when the survey has no
         * template matching the given answers.
         *
         * @param {number} instanceId - The survey answer instance id.
         *
         * @return {Promise<SurveySecureMessagingPrefill[]>}
         */
        async surveyPrefill(instanceId: number): Promise<SurveySecureMessagingPrefill[]> {
            return apiClient.get(`/survey-answer-instances/${instanceId}/secure-messaging`)
                .then((response: AxiosResponse) => response.data.data.audiences);
        },

        /**
         * Download a document declared by a template, to attach it to a message.
         *
         * @param {string} url - The API path streaming the file.
         *
         * @return {Promise<Blob>}
         */
        async document(url: string, source: SecureMessagingDocumentSource = 'api'): Promise<Blob> {
            const client = source === 'hds' ? hdsClient : apiClient;

            return client.get(url, {responseType: 'blob'})
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Send an email through MSSanté.
         *
         * @param {SendSecureMessageParams} params - The message to send.
         *
         * @return {Promise<{sent: boolean}>}
         */
        async send(params: SendSecureMessageParams): Promise<{ sent: boolean }> {
            const formData = new FormData();

            formData.append("to", params.to);
            formData.append("subject", params.subject);
            formData.append("body", params.body);

            (params.cc ?? []).forEach((cc) => formData.append("cc[]", cc));
            (params.attachments ?? []).forEach((attachment) => {
                formData.append("attachments[]", attachment.blob, attachment.name);
            });
            appendHistoryContext(formData, params);

            return hdsClient.post("/secure-messaging/messages", formData)
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Send an email to the patient Mon espace santé mailbox. The recipient
         * is resolved by the HDS server from the patient INS, which never
         * transits through the client.
         *
         * @param {number} customerId - The patient customer id.
         * @param {SendPatientSecureMessageParams} params - The message to send.
         *
         * @return {Promise<{sent: boolean}>}
         */
        async sendToPatient(
            customerId: number,
            params: SendPatientSecureMessageParams
        ): Promise<{ sent: boolean }> {
            const formData = new FormData();

            formData.append("subject", params.subject);
            formData.append("body", params.body);

            (params.attachments ?? []).forEach((attachment) => {
                formData.append("attachments[]", attachment.blob, attachment.name);
            });
            appendHistoryContext(formData, params);

            return hdsClient.post(`/secure-messaging/customers/${customerId}/messages`, formData)
                .then((response: AxiosResponse) => response.data.data);
        }
    };
}
